import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, describeError } from '../api';
import { Json, Message, Pill, Section } from '../components/ui';
import { useSession } from '../session';
import { SubscriptionSection, applyTierRefresh, mockBuyPack } from './Subscription';

/** change 11: GET /api/me/credits */
interface Summary {
  balance: number;
  currency: 'credits';
  daily: { reward: number; claimed_today: boolean; streak: number; next_claim_at: string | null };
  month: { month: string; consumed: number; by_model: Array<{ model_id: string; display_name: string; credits: number; share: number }> };
  packs: Array<{ id: string; usd: number; credits: number; bonus_pct: number; tag?: string; store_product_ids?: { ios?: string; android?: string; paypal?: string } }>;
  policy: { credits_per_usd: number; ledger_window_days: number };
}
interface Totals { balance: number; income: number; consumed: number; currency: 'credits' }
interface Tx { id: string; type: string; amount: number; balance_after: number; created_at: string; model_id?: string | null; ref: Record<string, unknown> }
interface Page { items: Tx[]; next_cursor: string | null }
interface AdminWallet extends Summary { user_id: string; available: number; reservations: Array<{ id: string; request_id: string; turn_index: number; model_id: string; amount: number; created_at: string; expires_at: string }> }

const TYPE_LABEL: Record<string, string> = { consume: '消耗', daily: '每日签到', recharge: '充值', adjust: '调账', refund: '退款', signup: '注册赠送' };
const fmt = (iso: string) => new Date(iso).toLocaleString();

function txTitle(t: Tx): string {
  const ref = t.ref as { model_display_name?: string; character_name?: string; source?: string; note?: string; streak?: number };
  const model = ref.model_display_name ?? t.model_id ?? '模型';
  const who = ref.character_name?.trim() || '未命名';
  if (t.type === 'consume' && typeof ref.source === 'string' && ref.source.startsWith('character_gen')) {
    const kind = { generate: 'AI 造卡', complete: 'AI 补全', regenerate: 'AI 重生成' }[ref.source.split(':')[1] ?? ''] ?? 'AI 造卡';
    return `${model} · ${kind}「${who}」`;
  }
  if (t.type === 'consume' && ref.source === 'summarize') return `${model} · 记忆总结「${who}」`;
  if (t.type === 'consume') return `${model} · 与「${who}」对话`;
  if (t.type === 'daily') return `每日签到（连签 ${ref.streak ?? '?'} 天）`;
  if (t.type === 'recharge') return `充值 · ${ref.source ?? ''}`;
  if (t.type === 'adjust' || t.type === 'refund') return `${TYPE_LABEL[t.type]} · ${ref.note ?? ref.source ?? ''}`;
  return TYPE_LABEL[t.type] ?? t.type;
}

function countdown(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return '可领取';
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `${h} 小时 ${m} 分后可领`;
}

export function CreditsPage() {
  const { me, isAdmin, reloadMe } = useSession();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [mockIap, setMockIap] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState<string | null>(null);
  const [buyPlatform, setBuyPlatform] = useState<'apple' | 'google'>('apple');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'income' | 'consume'>('all');
  const [items, setItems] = useState<Tx[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<Tx | null>(null);
  // admin
  const [targetUser, setTargetUser] = useState('');
  const [adminWallet, setAdminWallet] = useState<AdminWallet | null>(null);
  const [adjAmount, setAdjAmount] = useState('5500');
  const [adjType, setAdjType] = useState<'adjust' | 'recharge' | 'refund'>('recharge');
  const [adjNote, setAdjNote] = useState('staging test');
  const [adjOrder, setAdjOrder] = useState('');

  const report = (err: unknown) => setError(err instanceof ApiFailure ? describeError(err) : String(err));

  const load = useCallback(async () => {
    if (!me) return;
    setError(null);
    try {
      setSummary((await api<Summary>('/api/me/credits')).data);
      setTotals((await api<Totals>('/api/me/credits/totals')).data);
      const products = await api<{ mock_iap: boolean; paypal_client_id: string | null }>('/api/billing/products');
      setMockIap(products.data.mock_iap);
      setPaypalClientId(products.data.paypal_client_id);
    } catch (err) {
      report(err);
    }
  }, [me]);

  const loadTx = useCallback(async (type: 'all' | 'income' | 'consume', before: string | null, append: boolean) => {
    if (!me) return;
    try {
      const q = new URLSearchParams({ type, limit: '20' });
      if (before) q.set('before', before);
      const { data } = await api<Page>(`/api/me/credits/transactions?${q.toString()}`);
      setItems((prev) => (append ? [...prev, ...data.items] : data.items));
      setCursor(data.next_cursor);
    } catch (err) {
      report(err);
    }
  }, [me]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    void loadTx(filter, null, false);
  }, [filter, loadTx]);

  const refreshAll = async () => {
    await load();
    await loadTx(filter, null, false);
  };

  const claim = async () => {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const { data } = await api<{ amount: number; balance: number; streak: number; next_claim_at: string }>('/api/me/credits/daily-claim', { method: 'POST', body: {} });
      setMessage(`已领取 +${data.amount} 钻石 · 余额 ${data.balance} · 连签 ${data.streak} 天 · 下次 ${fmt(data.next_claim_at)}`);
      await refreshAll();
    } catch (err) {
      if (err instanceof ApiFailure && err.error.code === 'BILLING_ALREADY_CLAIMED') {
        const next = ((err.error as unknown as { next_claim_at?: string }).next_claim_at ?? (err.error as unknown as { details?: { next_claim_at?: string } }).details?.next_claim_at) ?? null;
        setMessage(`今日已领取 · ${countdown(next)}（${next ? fmt(next) : ''}）`);
        await load();
      } else report(err);
    } finally {
      setBusy(false);
    }
  };

  const buyPack = async (packId: string) => {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const r = await mockBuyPack(buyPlatform, packId);
      setMessage(r.granted ? `模拟购买 ${packId} · +${r.credits_added} 钻石 · 余额 ${r.balance} · 交易 ${r.transaction_id}` : `交易 ${r.transaction_id} 已处理过（幂等）`);
      await applyTierRefresh(r.token_refresh_required, reloadMe);
      await refreshAll();
    } catch (err) {
      report(err);
    } finally {
      setBusy(false);
    }
  };

  const buyPaypal = async (packId: string) => {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const created = await api<{ order_id: string; pack_id: string; status: string; approve_url: string | null }>('/api/billing/paypal/orders', { method: 'POST', body: { product_id: packId } });
      if (created.data.approve_url) {
        setMessage(`PayPal 订单 ${created.data.order_id} · ${created.data.approve_url}`);
        return;
      }
      const r = await api<{ granted: boolean; already_processed: boolean; transaction_id: string; credits_added?: number; balance?: number; token_refresh_required: boolean }>(`/api/billing/paypal/orders/${encodeURIComponent(created.data.order_id)}/capture`, { method: 'POST' });
      setMessage(r.data.granted ? `PayPal ${packId} · +${r.data.credits_added} 钻石 · 余额 ${r.data.balance} · 交易 ${r.data.transaction_id}` : `交易 ${r.data.transaction_id} 已处理过（幂等）`);
      await applyTierRefresh(r.data.token_refresh_required, reloadMe);
      await refreshAll();
    } catch (err) {
      report(err);
    } finally {
      setBusy(false);
    }
  };

  const loadAdmin = async () => {
    if (!targetUser.trim()) return;
    setError(null);
    try {
      setAdminWallet((await api<AdminWallet>(`/api/admin/wallets/${encodeURIComponent(targetUser.trim())}`)).data);
    } catch (err) {
      report(err);
    }
  };

  const adjust = async () => {
    if (!targetUser.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { amount: Number(adjAmount), type: adjType, note: adjNote || undefined };
      if (adjOrder) body.order_id = adjOrder;
      const { data } = await api<{ balance: number; transaction_id: string }>(`/api/admin/wallets/${encodeURIComponent(targetUser.trim())}/adjust`, { method: 'POST', body });
      setMessage(`调账完成 · 新余额 ${data.balance} · 流水 ${data.transaction_id}`);
      await loadAdmin();
      if (me && targetUser.trim() === me.id) await refreshAll();
    } catch (err) {
      report(err);
    } finally {
      setBusy(false);
    }
  };

  if (!me) return null;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-4">
        <SubscriptionSection onCreditsChanged={refreshAll} />
        <Section title="钻石余额 · GET /api/me/credits · totals" actions={<button className="btn-ghost" onClick={() => void refreshAll()}>刷新</button>}>
          <Message text={error} kind="error" />
          <Message text={message} kind="ok" />
          {summary && (
            <>
              <div className="flex items-end gap-3">
                <div className="text-4xl font-semibold text-amber-200">{summary.balance.toLocaleString()}</div>
                <div className="pb-1 text-xs text-zinc-400">钻石（API 字段 `credits`）· 永久有效 · 可透支（结算差额）· 新号首次访问入账 type=signup（当前 signup_bonus=200；已有钱包不补发）· 官方池现网按轮一口价（`credits_per_turn`），token 浮动公式保留不用</div>
              </div>
              {totals && (
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="rounded-lg border border-zinc-800 p-2"><div className="text-zinc-500">总余额</div><div className="text-lg text-amber-200">{totals.balance.toLocaleString()}</div></div>
                  <div className="rounded-lg border border-zinc-800 p-2"><div className="text-zinc-500">总收入</div><div className="text-lg text-emerald-300">{totals.income.toLocaleString()}</div></div>
                  <div className="rounded-lg border border-zinc-800 p-2"><div className="text-zinc-500">总消耗</div><div className="text-lg text-zinc-200">{totals.consumed.toLocaleString()}</div></div>
                </div>
              )}
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium text-zinc-200">每日签到 · {me.vip_tier === 'pro' ? 'Pro' : 'Free'} +{summary.daily.reward}</div>
                    <div className="text-xs text-zinc-400">连签 {summary.daily.streak} 天 · {summary.daily.claimed_today ? `今日已领 · ${countdown(summary.daily.next_claim_at)}` : '今日可领'} · UTC 日切</div>
                  </div>
                  <button className="btn" disabled={busy || summary.daily.claimed_today} onClick={() => void claim()}>领取 +{summary.daily.reward}</button>
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs text-zinc-400">本月消耗（{summary.month.month}）· 合计 {summary.month.consumed}</div>
                {summary.month.by_model.length === 0 && <div className="text-xs text-zinc-500">本月暂无消耗</div>}
                <div className="space-y-1">
                  {summary.month.by_model.map((m) => (
                    <div key={m.model_id} className="text-xs">
                      <div className="flex justify-between"><span>{m.display_name}</span><span className="text-zinc-400">{m.credits} · {Math.round(m.share * 100)}%</span></div>
                      <div className="h-1.5 rounded bg-zinc-800"><div className="h-1.5 rounded bg-amber-400/70" style={{ width: `${Math.max(2, m.share * 100)}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-1 flex items-center gap-2 text-xs text-zinc-400">
                  <span>充值包目录（credits_policy.packs）· $1 = {summary.policy.credits_per_usd} 钻石 · IAP 走 POST /api/billing/purchases/verify · Web PayPal 走 POST /api/billing/paypal/orders</span>
                  {mockIap && (['apple', 'google'] as const).map((p) => (
                    <button key={p} className={p === buyPlatform ? 'btn' : 'btn-ghost'} onClick={() => setBuyPlatform(p)}>{p}</button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {summary.packs.map((p) => (
                    <div key={p.id} className="rounded-lg border border-zinc-800 p-2 text-xs">
                      <div className="flex items-center justify-between"><span className="font-medium">${p.usd}</span>{p.tag && <Pill tone="amber">{p.tag}</Pill>}</div>
                      <div className="text-zinc-300">{p.credits.toLocaleString()} 钻石{p.bonus_pct ? ` · +${p.bonus_pct}%` : ''}</div>
                      <div className="mt-1 font-mono text-[10px] text-zinc-500">paypal {p.store_product_ids?.paypal ?? '—'}</div>
                      {mockIap && <button className="btn-ghost mt-1" disabled={busy} onClick={() => void buyPack(p.id)}>模拟购买</button>}
                      {(mockIap || paypalClientId) && <button className="btn-ghost mt-1 ml-1" disabled={busy} onClick={() => void buyPaypal(p.id)}>PayPal 买</button>}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Section>

        {isAdmin && (
          <Section title="运营 · /api/admin/wallets/{user_id}（admin_role）">
            <div className="flex gap-2">
              <input className="input flex-1" placeholder="user_id（留空 = 我自己）" value={targetUser} onChange={(e) => setTargetUser(e.target.value)} />
              <button className="btn-ghost" onClick={() => { if (!targetUser.trim()) setTargetUser(me.id); void loadAdmin(); }}>查看</button>
              <button className="btn-ghost" onClick={() => setTargetUser(me.id)}>用我的 id</button>
            </div>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <input className="input" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} placeholder="amount（±整数）" />
              <select className="input" value={adjType} onChange={(e) => setAdjType(e.target.value as typeof adjType)}>
                <option value="recharge">recharge（模拟充值）</option>
                <option value="adjust">adjust</option>
                <option value="refund">refund</option>
              </select>
              <input className="input" value={adjNote} onChange={(e) => setAdjNote(e.target.value)} placeholder="note" />
              <input className="input" value={adjOrder} onChange={(e) => setAdjOrder(e.target.value)} placeholder="order_id（可选）" />
            </div>
            <button className="btn" disabled={busy || !targetUser.trim()} onClick={() => void adjust()}>调账</button>
            <p className="text-xs text-zinc-500">staging 验证流程：给自己充 5500 → 官方模型聊一句看 message_end.credits_charged → 把余额调到 0（负数 adjust）→ 再聊一句得到 402 BILLING_INSUFFICIENT_CREDITS。</p>
            {adminWallet && <Json value={{ user_id: adminWallet.user_id, balance: adminWallet.balance, available: adminWallet.available, reservations: adminWallet.reservations, month: adminWallet.month }} className="max-h-72" />}
          </Section>
        )}
      </div>

      <div className="space-y-4">
        <Section
          title="钻石流水 · GET /api/me/credits/transactions"
          actions={
            <div className="flex gap-1">
              {(['all', 'income', 'consume'] as const).map((f) => (
                <button key={f} className={f === filter ? 'btn' : 'btn-ghost'} onClick={() => setFilter(f)}>{f === 'all' ? '全部' : f === 'income' ? '收入' : '消耗'}</button>
              ))}
            </div>
          }
        >
          {items.length === 0 && <div className="text-xs text-zinc-500">当前筛选下暂无流水（默认最近 {summary?.policy.ledger_window_days ?? 30} 天）</div>}
          <ul className="divide-y divide-zinc-800 text-sm">
            {items.map((t) => (
              <li key={t.id} className="flex cursor-pointer items-center justify-between gap-3 py-2" onClick={() => setDetail(detail?.id === t.id ? null : t)}>
                <div className="min-w-0">
                  <div className="truncate">{txTitle(t)}</div>
                  <div className="text-xs text-zinc-500">{fmt(t.created_at)}{typeof t.ref.total_tokens === 'number' ? ` · ${t.ref.total_tokens} tokens` : ''} · {TYPE_LABEL[t.type] ?? t.type}</div>
                </div>
                <div className={`shrink-0 font-mono ${t.amount < 0 ? 'text-zinc-200' : 'text-emerald-300'}`}>{t.amount > 0 ? `+${t.amount}` : t.amount}</div>
              </li>
            ))}
          </ul>
          {cursor && <button className="btn-ghost" onClick={() => void loadTx(filter, cursor, true)}>加载更早</button>}
          {detail && <Json value={detail} className="max-h-64" />}
        </Section>
      </div>
    </div>
  );
}
